import { Input } from '@payaca/ambrosia';
import { isEqual } from 'lodash-es';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import * as invoicesActions from '@payaca/store/invoices/invoicesActions';
import { UpdateInvoiceRequestData } from '@payaca/store/invoices/invoicesTypes';

import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getDeal,
  getInvoice,
  getInvoiceLinesByInvoiceId,
  getJobLineItemsByInvoiceId,
} from '@/utils/stateAccessors';

import CollapsiblePanel, {
  CollapsiblePanelStyleVariant,
} from '@payaca/components/collapsiblePanel/CollapsiblePanel';
import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';

import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getCustomerInvoicedValueIncludingDrafts } from '@payaca/helpers/grantHelper';
import { getInvoiceTotalBreakdown } from '@payaca/helpers/invoiceHelper';

import { Deal } from '@payaca/types/dealTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { Invoice } from '@payaca/types/invoiceTypes';

import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { usePaymentReconciliationRecordsForDeal } from '@payaca/store/hooks/appState';
import DealPaymentOptionsControl from '../dealPaymentOptionsControl/DealPaymentOptionsControl';
import DealPriceDetailControl from '../dealPriceDetailControl/DealPriceDetailControl';
import { ErrorBoundary } from '../errorBoundary';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import { ProjectAccountingIntegrationCategoriesControl } from '../projectAccountingIntegrationCategoriesControl/ProjectAccountingIntegrationCategoriesControl';
import VoidInvoiceButton from '../voidInvoiceButton/VoidInvoiceButton';
import './InvoiceSidePanel.sass';

const getInitialFormState = (invoice?: Invoice) => {
  return {
    invoiceId: invoice?.id,
    customReference: invoice?.customReference,
    notes: invoice?.notes,
    dueInDays: invoice?.dueInDays,
    assignedToUserId: invoice?.assignedToUserId,
    contactId: invoice?.contactId,
  };
};

type Props = {
  invoiceId: number;
};

const InvoiceSidePanel: FC<Props> = ({
  invoiceId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();

  const [isOpen, setIsOpen] = useState(true);

  const invoice: Invoice | undefined = useSelector((state) => {
    return getInvoice(state, invoiceId);
  });

  const deal: Deal | undefined = useSelector((state) => {
    if (!invoice) return;
    return getDeal(state, invoice.dealId);
  });

  const paymentReconciliationRecords = usePaymentReconciliationRecordsForDeal(
    invoice?.dealId
  );

  const unreconciledTotal = paymentReconciliationRecords
    .filter((record) => !record.invoiceId)
    .reduce((acc, record) => acc + record.value, 0);

  const region = useSelector(
    (state: any) => state.users.myProfile.accounts[0].region
  );

  const isUpdatingInvoice: boolean = useSelector((state) => {
    return state.invoices.isUpdatingInvoice;
  });

  const isFetchingInvoice: boolean = useSelector((state) => {
    return (
      state.invoices.invoices && state.invoices.invoices[invoiceId]?.isFetching
    );
  });

  const invoiceLines = useSelector((state) => {
    return getInvoiceLinesByInvoiceId(state, invoiceId);
  });

  const jobLineItems = useSelector((state) => {
    return getJobLineItemsByInvoiceId(state, invoiceId);
  });

  const onInvoiceUpdateSuccess = useCallback(() => {
    dispatch(invoicesActions.requestGetInvoice(invoiceId));
  }, [dispatch, invoiceId]);

  const initialFormState = useMemo(() => {
    return getInitialFormState(invoice);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'invoice'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: invoice.id
  }, [invoice?.id]);

  const requiresUpdateInvoice = useCallback(
    (formState: { [key: string]: any }) => {
      if (!invoice) return false;
      if (isFetchingInvoice || isUpdatingInvoice) return true;
      return !isEqual(formState, getInitialFormState(invoice));
    },
    [isFetchingInvoice, isUpdatingInvoice, invoice]
  );

  const updateInvoice = useCallback(
    (formState: { [key: string]: any }) => {
      const updateInvoiceRequestData = formState as UpdateInvoiceRequestData;

      if (updateInvoiceRequestData && updateInvoiceRequestData.invoiceId) {
        if (requiresUpdateInvoice(formState)) {
          dispatch(
            invoicesActions.requestUpdateInvoice(
              updateInvoiceRequestData,
              onInvoiceUpdateSuccess
            )
          );
        }
      }
    },
    [dispatch, onInvoiceUpdateSuccess, requiresUpdateInvoice]
  );

  const invoiceTotalBreakdown = useMemo(() => {
    return getInvoiceTotalBreakdown(invoiceLines, jobLineItems);
  }, [invoiceLines, jobLineItems]);

  const { project } = useGetProjectByIdOverview(deal?.publicId);

  const projectSummary = useMemo(() => {
    if (!deal || !invoice) return;
    const totals = project?.totals;
    const totalPayable = totals?.totalPayable.value ?? deal.acceptedValue;
    const customerInvoicedValue = totals
      ? getCustomerInvoicedValueIncludingDrafts({
          invoicedValue: totals.invoicedValue.value,
          sentInvoicedValue: totals.sentInvoicedValue.value,
          customerInvoicedValue: totals.customerInvoicedValue.value,
        })
      : deal.invoicedValue;

    return {
      grantTotal: Math.max(0, deal.acceptedValue - totalPayable),
      invoicedToDate: customerInvoicedValue - invoice.totalValue,
      stillToInvoice: totalPayable - customerInvoicedValue,
    };
  }, [deal, invoice, project?.totals]);

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      if (!invoice) return <></>;

      return (
        <div className="invoice-side-panel-form-contents mb-8">
          <div className="invoice-due-days-wrapper">
            Invoice due in{' '}
            <Input
              className="w-16 inline-block align-middle"
              sizeVariant="sm"
              name="dueInDays"
              value={formState.dueInDays}
              // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
              type="number"
              onChange={(value: string) => {
                const updateObject: { [key: string]: any } = {
                  dueInDays: undefined,
                };
                if (!isNaN(Number(value)) && value !== '') {
                  updateObject['dueInDays'] = Math.round(Number(value));
                }
                onFieldChange(updateObject);
              }}
              changeTimeoutMs={2000}
              onBlur={() => updateInvoice(formState)}
              onChangeTimeout={(value: string) => {
                const dueInDays =
                  !isNaN(Number(value)) && value !== ''
                    ? Math.round(Number(value))
                    : undefined;
                updateInvoice({ ...formState, dueInDays });
              }}
              disabled={
                !userHasRequiredPermission([
                  InvoicesPermissions.EDIT_INVOICE_DUE_IN_DAYS,
                ])
              }
            />{' '}
            days
          </div>
          <div className="invoice-and-deal-totals">
            <div className="invoice-totals-container">
              <LabelValuePair
                suffixLabelWith=""
                label="Subtotal"
                value={currencyPrice(invoiceTotalBreakdown.subtotal, region)}
              />
              <LabelValuePair
                suffixLabelWith=""
                label="Tax"
                value={currencyPrice(invoiceTotalBreakdown.vatTotal, region)}
              />

              {!!invoiceTotalBreakdown.cisTotal && (
                <LabelValuePair
                  suffixLabelWith=""
                  label="CIS suffered"
                  value={`${
                    invoiceTotalBreakdown.cisTotal * -1 >= 0 ? '+' : ''
                  }${currencyPrice(
                    invoiceTotalBreakdown.cisTotal * -1,
                    region
                  )}`}
                />
              )}
              <hr />
              <LabelValuePair
                suffixLabelWith=""
                label="Total"
                value={currencyPrice(invoice.totalValue, region)}
                className="total"
              />
            </div>
            {deal && projectSummary && (
              <CollapsiblePanel
                styleVariant={CollapsiblePanelStyleVariant.OUTSIZE}
                title={`Project #${
                  deal.reference || deal.customReference
                } summary`}
                isOpen={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
              >
                <LabelValuePair
                  suffixLabelWith=""
                  label="Accepted total"
                  value={currencyPrice(deal?.acceptedValue, region)}
                />
                {projectSummary.grantTotal > 0 ? (
                  <LabelValuePair
                    suffixLabelWith=""
                    label="Grants"
                    value={currencyPrice(-projectSummary.grantTotal, region)}
                  />
                ) : (
                  <></>
                )}
                <LabelValuePair
                  suffixLabelWith=""
                  label="Invoiced to date"
                  value={currencyPrice(projectSummary.invoicedToDate, region)}
                />
                <LabelValuePair
                  suffixLabelWith=""
                  label="Still to invoice"
                  value={currencyPrice(projectSummary.stillToInvoice, region)}
                />
                <hr />
                <>
                  {unreconciledTotal > 0 && (
                    <>
                      <LabelValuePair
                        suffixLabelWith=""
                        label="Unreconciled payment value"
                        value={currencyPrice(unreconciledTotal, region)}
                      />
                      {invoice.totalValue > 0 && (
                        <small>
                          Unreconciled payments will be reconciled against this
                          Invoice as soon as it is sent. This will{' '}
                          {unreconciledTotal >= invoice.totalValue
                            ? 'fully'
                            : 'partially'}{' '}
                          pay off the Invoice.
                        </small>
                      )}
                    </>
                  )}
                </>
              </CollapsiblePanel>
            )}
          </div>
        </div>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'userHasRequiredPermission', and 'isOpen'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: isUpdatingInvoice
    [
      projectSummary,
      invoice,
      updateInvoice,
      isUpdatingInvoice,
      invoiceTotalBreakdown,
      region,
      deal,
      unreconciledTotal,
    ]
  );

  return (
    <div className="invoice-side-panel">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
      />

      {invoice && (
        <>
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.MANAGE_PAYMENT_OPTIONS]}
          >
            <DealPaymentOptionsControl dealId={invoice?.dealId} />
          </PermissionGuard>
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.MANAGE_PRICE_VISIBILITY]}
          >
            <DealPriceDetailControl dealId={invoice.dealId} canHideVat={true} />
          </PermissionGuard>
          {deal && (
            <ErrorBoundary>
              <ProjectAccountingIntegrationCategoriesControl
                projectId={deal?.publicId}
              />
            </ErrorBoundary>
          )}
          <VoidInvoiceButton invoiceId={invoiceId} />
        </>
      )}
    </div>
  );
};
export default InvoiceSidePanel;
